import { useEffect, useState } from 'react'
import { useSpendingDemand } from '../../../hooks'
import Button from '../../../../../components/atoms/Button/Button'
import SelectInput from '../../../../../components/atoms/SelectInput/SelectInput'
import { Action } from '../../../hooks/core/useSpendingDemandCore'
import { numberToStingWithSpaces } from '../../../../../utils/numberDisplayer'
import { TextField, Box, Typography, Stack, LinearProgress, Alert, Chip } from '@mui/material'
import { SpendingDemand, SpendingDemandExtended } from '../../../types/spendingDemand.types'
import { JWayIcon } from '../../../../../assets/JWayIcons'
import { CheckCircle, Cancel, Edit, AccountBalance } from '@mui/icons-material'
import ExtractPresenter from '../Extract/ExtractPresenter'
import useNavigationApp from '../../../../../navigation/hook/useNavigationApp'

export default function SelectGroupedAction() {
    const [actionChoosen, setActionChoosen] = useState<Action>('VALIDATE')
    const { 
        spendingDemandSelected,
        groupedActions, 
        isLoadingActionGrouped, 
        groupedActionsProgress,
        groupedActionsMessage,
        setSpendingDemandSelectedList 
    } = useSpendingDemand()
    const { goToCreateCreditFromDemand } = useNavigationApp()

    const [remarks, setRemarks] = useState<string>('')
    
    const handleClick = () => {
        if (actionChoosen === 'CREATE_CREDIT') {
            // Pour CREATE_CREDIT, on navigue directement sans passer par l'API
            if (!spendingDemandSelected || spendingDemandSelected.length === 0) return
            
            // Récupérer tous les IDs et noms des spending demands sélectionnés
            const spendingDemandIds = spendingDemandSelected.map(demand => demand.spending_demand_id)
            const spendingDemandNames = spendingDemandSelected.map(demand => demand.spending_demand_desc || '')
            
            // Récupérer l'user_id (tous les spending demands ont le même utilisateur grâce à la validation)
            const userId = getUserId(spendingDemandSelected[0])
            
            // Naviguer vers la création de crédits avec tous les IDs, noms et l'user_id
        goToCreateCreditFromDemand(spendingDemandIds, spendingDemandNames, userId || undefined)
        } else {
            console.log('groupedActions', groupedActions)
            groupedActions(actionChoosen, remarks)
            setRemarks('')
            // Ne pas réinitialiser la sélection ici - elle sera faite dans le hook après le traitement
        }
    }
    
    const cancelHandleClick = () => {
        setSpendingDemandSelectedList([])
        setRemarks('')
    }

    // Vider les sélections immédiatement en cas de succès
    useEffect(() => {
        if (groupedActionsMessage.type === 'success') {
            setSpendingDemandSelectedList([])
        }
    }, [groupedActionsMessage.type, setSpendingDemandSelectedList])

    if (!spendingDemandSelected) return <></>

    if (spendingDemandSelected?.length === 0) return <></>

    const getActionIcon = (action: Action) => {
        switch (action) {
            case 'VALIDATE':
                return <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
            case 'REJECT':
                return <Cancel sx={{ fontSize: 16, color: '#f44336' }} />
            case 'REQUEST_CORRECTION':
                return <Edit sx={{ fontSize: 16, color: '#ff9800' }} />
            case 'CREATE_CREDIT':
                return <AccountBalance sx={{ fontSize: 16, color: '#1976d2' }} />
            default:
                return null
        }
    }

    const getActionColor = (action: Action) => {
        switch (action) {
            case 'VALIDATE':
                return '#4caf50'
            case 'REJECT':
                return '#f44336'
            case 'REQUEST_CORRECTION':
                return '#ff9800'
            case 'CREATE_CREDIT':
                return '#1976d2'
            default:
                return '#666666'
        }
    }

    // Fonction pour extraire l'user_id d'un spending demand
    const getUserId = (demand: SpendingDemand | SpendingDemandExtended): string | null => {
        if (typeof demand.spending_demand_user === 'string') {
            return demand.spending_demand_user
        } else if (demand.spending_demand_user && typeof demand.spending_demand_user === 'object' && 'user_id' in demand.spending_demand_user) {
            return demand.spending_demand_user.user_id
        }
        return null
    }

    // Vérifier si tous les spending demands ont le même utilisateur
    const areAllUsersSame = (): boolean => {
        if (!spendingDemandSelected || spendingDemandSelected.length === 0) return true
        if (spendingDemandSelected.length === 1) return true
        
        const userIds = spendingDemandSelected.map(demand => getUserId(demand)).filter(id => id !== null)
        if (userIds.length === 0) return false
        
        const firstUserId = userIds[0]
        return userIds.every(userId => userId === firstUserId)
    }

    // Vérifier si tous les spending demands ont le statut VALIDATE ou CREDITED
    const areAllStatusValidForCredit = (): boolean => {
        if (!spendingDemandSelected || spendingDemandSelected.length === 0) return false
        
        return spendingDemandSelected.every(demand => 
            demand.spending_demand_status === 'VALIDATE' || 
            demand.spending_demand_status === 'CREDITED'
        )
    }

    const isCreateCreditDisabled = actionChoosen === 'CREATE_CREDIT' && (!areAllUsersSame() || !areAllStatusValidForCredit())

    
  return (
    <Box sx={{ 
        border: '1px solid #e0e0e0', 
        borderRadius: 2, 
        p: 2, 
        backgroundColor: '#fafafa',
        minHeight: '200px',
        position: 'sticky',
        top: '20px'
    }}>
        {/* Header avec bouton de fermeture */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
                Actions groupées
            </Typography>
            <Box 
                onClick={cancelHandleClick}
                sx={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                        backgroundColor: '#e0e0e0'
                    }
                }}
            >
                <JWayIcon type="cross" color='gray' size={12} />
            </Box>
        </Box>

        {/* Si un message est affiché, ne montrer que le message */}
        {groupedActionsMessage.type ? (
            <Box sx={{ mb: 3 }}>
                <Alert 
                    severity={groupedActionsMessage.type} 
                    sx={{ 
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                            width: '100%'
                        }
                    }}
                >
                    {groupedActionsMessage.message}
                </Alert>
            </Box>
        ) : (
            <>
                {/* Informations sur la sélection */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                    <Stack spacing={1}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            <strong>{spendingDemandSelected.length}</strong> {spendingDemandSelected.length > 1 ? "demandes sélectionnées" : "demande sélectionnée"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            Montant total: <strong>{numberToStingWithSpaces(spendingDemandSelected.reduce((sum, item) => sum + (item.spending_demand_amountTTC|| 0), 0))} FCFA</strong>
                        </Typography>
                    </Stack>
                </Box>

                {/* Composant Extract */}
                <ExtractPresenter />

                {/* Sélection de l'action */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
                        Choisir une action
                    </Typography>
                    <SelectInput
                        label='Action'
                        options={[
                            {
                                label: 'Valider',
                                value: 'VALIDATE'
                            },
                            {
                                label: 'Refuser',
                                value: 'REJECT'
                            },
                            {
                                label: 'Demander une correction',
                                value: 'REQUEST_CORRECTION'
                            },
                            {
                                label: 'Créer des avances',
                                value: 'CREATE_CREDIT'
                            }
                        ]}
                        value={actionChoosen}
                        handleChange={(value) => {setActionChoosen(value)}}
                    />
                    
                    {/* Indicateur visuel de l'action sélectionnée */}
                    {actionChoosen !== 'CREATE_CREDIT' && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getActionIcon(actionChoosen)}
                            <Typography variant="body2" sx={{ color: getActionColor(actionChoosen), fontWeight: 500 }}>
                                {actionChoosen === 'VALIDATE' && 'Validation des demandes'}
                                {actionChoosen === 'REJECT' && 'Refus des demandes'}
                                {actionChoosen === 'REQUEST_CORRECTION' && 'Demande de correction'}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Champ de remarque - masqué pour CREATE_CREDIT */}
                {actionChoosen !== 'CREATE_CREDIT' && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
                            Remarque {actionChoosen === 'REQUEST_CORRECTION' && '(obligatoire)'}
                        </Typography>
                        <TextField
                            id="remarks-field"
                            label={actionChoosen === 'REQUEST_CORRECTION' ? "Précisez les corrections à apporter" : "Ajouter une remarque"}
                            multiline
                            rows={3}
                            fullWidth
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            variant="outlined"
                            size="small"
                            required={actionChoosen === 'REQUEST_CORRECTION'}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#fff'
                                }
                            }}
                        />
                    </Box>
                )}

                {/* Message d'erreur si les utilisateurs ne sont pas les mêmes ou si les statuts ne sont pas valides pour CREATE_CREDIT */}
                {actionChoosen === 'CREATE_CREDIT' && isCreateCreditDisabled && (
                    <Box sx={{ mb: 3 }}>
                        <Alert 
                            severity="warning" 
                            sx={{ 
                                borderRadius: 2,
                                '& .MuiAlert-message': {
                                    width: '100%'
                                }
                            }}
                        >
                            <Typography variant="body2">
                                {!areAllUsersSame() && 'Toutes les demandes sélectionnées doivent appartenir au même utilisateur pour créer des avances.'}
                                {areAllUsersSame() && !areAllStatusValidForCredit() && 'Seules les demandes avec le statut "Validée" ou "Créditée" peuvent être utilisées pour créer des avances.'}
                            </Typography>
                        </Alert>
                    </Box>
                )}

                {/* Barre de progression */}
                {isLoadingActionGrouped && groupedActionsProgress.total > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                                Traitement en cours...
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                                {groupedActionsProgress.current}/{groupedActionsProgress.total}
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={(groupedActionsProgress.current / groupedActionsProgress.total) * 100}
                            sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                backgroundColor: '#f0f0f0',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                }
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            {groupedActionsProgress.completed > 0 && (
                                <Chip 
                                    label={`${groupedActionsProgress.completed} succès`} 
                                    size="small" 
                                    color="success" 
                                    variant="outlined"
                                />
                            )}
                            {groupedActionsProgress.failed > 0 && (
                                <Chip 
                                    label={`${groupedActionsProgress.failed} échecs`} 
                                    size="small" 
                                    color="error" 
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Bouton d'action */}
                <Button 
                    onClick={handleClick} 
                    label={
                        actionChoosen === 'VALIDATE' ? 'Valider les demandes' :
                        actionChoosen === 'REJECT' ? 'Refuser les demandes' :
                        actionChoosen === 'CREATE_CREDIT' ? 'Créer les avances' :
                        'Demander correction'
                    }
                    isLoading={isLoadingActionGrouped}
                    disabled={
                        isLoadingActionGrouped || 
                        (actionChoosen === 'REQUEST_CORRECTION' && !remarks.trim()) ||
                        isCreateCreditDisabled
                    }
                />
            </>
        )}
    </Box>
  )
}
